import React, {useRef} from 'react';
import {useState} from 'react';
import {Button} from '~/components/Button';
import {fetchTags} from '~/components/Controls/Tags/api';
import {Intent} from '~/lib/intent';
import {classByIntent} from '~/lib/intent';
import {useResource} from '~/lib/loader';
import {useOutsideClick} from '~/lib/outsideClick';
import style from './index.module.scss';

export interface ITagsInputProps {
    value: string[];
    onChange: (value: string[]) => void;
    intent?: Intent;
    size?: 'default' | 'small';
    disabled?: boolean;
    dropdown?: boolean;
    onFocusChange?: (focused: boolean) => void;
    onPendingChange?: (value: string) => void;
}

type ITagsState = {
    isOpen: boolean;
    filter?: string;
};

export const TagsInput = ({value: values, onChange, dropdown, disabled, onFocusChange, onPendingChange, intent, size = 'default'}: ITagsInputProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    let [state, setState] = useState<ITagsState>({
        isOpen: false,
        filter: ''
    });

    const {resource} = useResource(() => fetchTags(), []);

    useOutsideClick(contentRef, () => setState({...state, isOpen: false}));

    const addTag = (rawTag: string): boolean => {
        const tag = rawTag.trim();
        if (!tag) {
            return false;
        }
        if ((values || []).includes(tag)) {
            return false;
        }
        onChange((values || []).concat(tag));
        return true;
    };

    const tagsFilter = (tag: string) => {
        if ((values || []).includes(tag)) {
            return false;
        }
        if (state.filter && !tag.toLowerCase().includes(state.filter.toLowerCase())) {
            return false;
        }
        return true;
    };

    let tagsClasses = [style.tags, classByIntent(intent)];

    if (disabled) {
        tagsClasses.push(style.disabled);
    }

    const tagValueClasses = [style.tagsValue];
    if (size === 'small') {
        tagValueClasses.push(style.tagsValueSmall);
    }
    const tagValueClassName = tagValueClasses.join(' ');

    return <div className={style.tagsWrapper} ref={contentRef}>
        <div className={tagsClasses.join(' ')} onClick={() => !disabled && inputRef.current?.focus()}>
            <div className={style.tagsValues}>
                {(values || []).map((value, index) => {
                    return (
                        <span key={index} className={tagValueClassName}>
                            {value}
                            {!disabled && <i className={['fa fa-close', style.tagsRemove].join(' ')} onClick={() => {
                                onChange((values || []).filter((_, valueIndex) => index !== valueIndex));
                            }}/>}
                        </span>
                    );
                })}
                <input
                    className={style.tagsInput}
                    ref={inputRef}
                    onFocus={() => {
                        if (disabled) {
                            return;
                        }
                        setState({...state, isOpen: true});
                        onFocusChange && onFocusChange(true);
                    }}
                    onKeyDown={(event) => {
                        const target = event.target as HTMLInputElement;
                        if (event.keyCode === 13) {
                            event.preventDefault();
                            if (addTag(target.value)) {
                                target.value = '';
                                onPendingChange && onPendingChange('');
                            }
                        }
                        setState({...state, filter: target.value});
                    }}
                    onKeyUp={(event) => {
                        const target = event.target as HTMLInputElement;
                        setState({...state, filter: target.value});
                        onPendingChange && onPendingChange(target.value);
                    }}
                    onBlur={() => {
                        onPendingChange && onPendingChange('');
                        onFocusChange && onFocusChange(false);
                    }}
                />
            </div>
            {(values || []).length > 0 && !disabled &&
                <i className={['fa fa-close', style.tagsClear].join(' ')} onClick={() => onChange([])}/>
            }
        </div>
        {(dropdown && state.isOpen) &&
        <div className={style.suggestTags}>
            {(resource.filter(tagsFilter) || []).map((tag, index) => <div key={index} className={style.suggestValue} onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (addTag(tag as unknown as string) && inputRef.current) {
                    inputRef.current.value = '';
                    inputRef.current.focus();
                }
            }}>{tag}</div>)}
        </div>
        }
    </div>;
};

export const tagListData = (item) => {
    let newArray: string[] = [];
    if (item == null) {
        return newArray;
    } else if (typeof item === 'string') {
        if (item.length) {
            newArray = item.split('\n');
        } else {
            return newArray;
        }
        return newArray.filter(el => el);
    } else {
        return item;
    }
};

export interface ITagsListProps {
    values: string[];
    maxItems?: number;
    size?: 'default' | 'small';
}

export const TagsList = ({values, maxItems, size = 'default'}: ITagsListProps) => {
    const [expanded, setExpanded] = useState<boolean>(false);
    return <span className={style.tagsList}>
        {typeof values === 'object' ? values.map((value, index) => {
            if (maxItems && !expanded && (index >= maxItems || (index === maxItems - 1 && values.length !== maxItems))) {
                return null;
            }
            const classes = [style.tagsValue];
            if (size === 'small') {
                classes.push(style.tagsValueSmall);
            }
            return (
                <span
                    className={classes.join(' ')}
                    key={`value-${index}`}
                >
                    {value}
                </span>
            );
        }) : ''}
        {typeof values === 'object' && maxItems && values.length > maxItems && !expanded ? <Button small
            responsive
            className={style.buttonDashedLink}
            onClick={() => setExpanded(true)}
        >+ {(values.length - maxItems < 2 ? 2 : values.length - maxItems + 1)} more</Button> : ''}
    </span>;
};
